import { Input, InputNumber, TableProps } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatCurrency } from 'utils';

import { Button, Spin, Table, Switch } from 'components/common';
import { Select, Option } from 'components/common/select';

import { useDsp } from 'modules/dsp/useDsp';
import NotificationService from 'modules/notifications/notificationService';
import { useOrganization } from 'modules/organization/useOrganization';

import { useAdformMediaTypesQuery } from '../../modules/apiData/dataApiSlice';
import { AdformEntityType, ADFORM_ENTITY_OPTIONS } from '../../modules/dsp/adformEntityType';
import {
  DspInsertionOrder,
  DspInsertionOrderUpdate,
  DspLineItem,
  DspLineItemUpdate,
} from '../../modules/dsp/dspApiTypes';
import { FREEWHEEL_ENTITY_OPTIONS } from '../../modules/dsp/freewheelEntityType';
import {
  DSP_ADFORM,
  DSP_AMAZON,
  DSP_CRITEO,
  DSP_FREEWHEEL,
  DSP_VISTAR,
} from '../../utils/constants';

const PRICE_TYPE_OPTIONS = [
  { value: 'ADDED', label: 'ADDED' },
  { value: 'CPM', label: 'CPM' },
];

type TableData = {
  id: string;
  enabled: boolean;
  name?: string | null;
  displayName: string;
  cpm?: number;
  cpc?: number;
  budgetCap?: number;
  priceType?: string;
  mediaType?: string;
  sourceMediaType?: string;
  audienceName?: string;
  audienceDisplayName?: string;
  children?: DspLineItem[];
};

export const DspSettings = () => {
  const { dsp, currentAdvertiserId, currentAdvertiser } = useOrganization();

  const priceFieldName = dsp === DSP_CRITEO ? 'cpc' : 'cpm';

  const getDspSettingsTitle = (dsp: string | null) => {
    switch (dsp) {
      case DSP_VISTAR:
        return 'Vistar Settings';
      case DSP_CRITEO:
        return 'Criteo Settings';
      case DSP_ADFORM:
        return 'Adform Settings';
      case DSP_AMAZON:
        return 'Amazon Settings';
      case DSP_FREEWHEEL:
        return 'FreeWheel Settings';
      default:
        return 'DSP Settings';
    }
  };
  const {
    getInsertionOrders,
    insertionOrders,
    updateLineItems,
    updateInsertionOrders,
    updateStatus,
    refreshDspMetadata,
    isLoading,
  } = useDsp();

  const { data: adformMediaTypes } = useAdformMediaTypesQuery(undefined, {
    skip: dsp !== DSP_ADFORM && dsp !== DSP_FREEWHEEL,
  });

  const [hasDrafts, setHasDrafts] = useState(false);
  const [draftLIs, setDraftLIs] = useState<Record<string, Partial<DspLineItemUpdate>>>({});
  const [draftCampaigns, setDraftCampaigns] = useState<
    Record<string, Partial<DspInsertionOrderUpdate>>
  >({});
  // string, not AdformEntityType: the same control now drives two enums.
  // ADVERTISERS is the one value both share, so it is a safe default for either.
  const [selectedEntityType, setSelectedEntityType] = useState<string>(
    AdformEntityType.ADVERTISERS,
  );

  // Adform's 'deals'/'tags' do not exist for FreeWheel (and vice versa), so a
  // stale selection would post an entity the target DSP rejects with a 400.
  useEffect(() => {
    setSelectedEntityType(AdformEntityType.ADVERTISERS);
  }, [dsp]);

  const supportsMetadataSync = dsp === DSP_ADFORM || dsp === DSP_FREEWHEEL;

  // FreeWheel line-item edits (CPM, price type, media type) land in
  // FW_LINE_ITEM's override columns, which the metadata sync carries forward,
  // so they are editable exactly as for Adform.
  const lineItemNamesEditable = dsp === DSP_ADFORM || dsp === DSP_FREEWHEEL;
  const entityOptions = dsp === DSP_FREEWHEEL ? FREEWHEEL_ENTITY_OPTIONS : ADFORM_ENTITY_OPTIONS;
  const [searchText, setSearchText] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const columnWidthsRef = useRef(columnWidths);

  columnWidthsRef.current = columnWidths;

  useEffect(() => {
    if (currentAdvertiserId) {
      getInsertionOrders(currentAdvertiserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAdvertiserId]);

  useEffect(() => {
    setDraftLIs({});
    setDraftCampaigns({});
  }, [insertionOrders]);

  useEffect(() => {
    setHasDrafts(Object.keys(draftLIs).length > 0 || Object.keys(draftCampaigns).length > 0);
  }, [draftLIs, draftCampaigns]);

  const saveChanges = () => {
    const lineItemUpdates: DspLineItemUpdate[] = Object.entries(draftLIs).map(([id, li]) => ({
      id,
      ...li,
    }));

    const campaignUpdates: DspInsertionOrderUpdate[] = Object.entries(draftCampaigns).map(
      ([id, campaign]) => ({
        id,
        ...campaign,
      }),
    );

    // Validate price values
    for (const update of lineItemUpdates) {
      if (update.cpm !== undefined && update.cpm < 0) {
        NotificationService.warn('Ensure all CPMs are positive');

        return;
      }

      if (update.cpc !== undefined && update.cpc < 0) {
        NotificationService.warn('Ensure all CPCs are positive');

        return;
      }
    }

    for (const update of campaignUpdates) {
      if (update.budgetCap !== undefined && update.budgetCap < 0) {
        NotificationService.warn('Ensure all Budget Caps are positive');

        return;
      }
    }

    // Filter out empty updates
    const filteredCampaignUpdates = campaignUpdates.filter(
      (c) => Object.keys(c).length > 1, // more than just 'id'
    );
    const filteredLineItemUpdates = lineItemUpdates.filter(
      (li) => Object.keys(li).length > 1, // more than just 'id'
    );

    if (filteredCampaignUpdates.length > 0) {
      updateInsertionOrders(filteredCampaignUpdates);
    }

    if (filteredLineItemUpdates.length > 0) {
      updateLineItems(filteredLineItemUpdates);
    }
  };

  const handleRefresh = () => {
    if (!dsp) return;
    refreshDspMetadata(dsp, selectedEntityType);
  };

  const rows = useMemo<TableData[]>(() => {
    if (!insertionOrders) return [];

    return insertionOrders.map((io) => {
      const childrenWithDrafts =
        io.lineItems?.map((li) => {
          const draft = draftLIs[li.id];

          return {
            ...li,
            displayName: draft?.displayName ?? li.displayName ?? li.name ?? '',
            priceType: draft?.priceType ?? li.priceType,
            mediaType: draft?.mediaType ?? li.mediaType,
            audienceName: draft?.audienceName ?? li.audienceName,
            audienceDisplayName: draft?.audienceDisplayName ?? li.audienceDisplayName,
            cpm: draft?.cpm ?? li.cpm,
            cpc: draft?.cpc ?? li.cpc,
          };
        }) || [];

      const campaignDraft = draftCampaigns[io.id];

      return {
        id: io.id,
        name: io.name,
        enabled: io.enabled ?? false,
        displayName: campaignDraft?.displayName ?? io.displayName ?? io.name ?? '',
        [priceFieldName]: io[priceFieldName as keyof DspInsertionOrder] ?? 0,
        budgetCap: campaignDraft?.budgetCap ?? io.budgetCap ?? 0,
        children: childrenWithDrafts.length > 0 ? childrenWithDrafts : undefined,
      };
    });
  }, [insertionOrders, draftLIs, draftCampaigns, priceFieldName]);

  const resizableHeaderCell = useCallback(
    (colKey: string, defaultWidth: number) => () => ({
      className: 'dsp-resizable-th',
      onMouseDown: (e: React.MouseEvent<HTMLTableCellElement>) => {
        const th = e.currentTarget as HTMLElement;
        const rect = th.getBoundingClientRect();
        const isResizeZone = e.clientX > rect.right - 8;

        if (!isResizeZone) return;

        e.preventDefault();
        e.stopPropagation();

        const startX = e.clientX;
        const startWidth = columnWidthsRef.current[colKey] ?? defaultWidth;
        const colGroup = th.closest('table')?.querySelector('colgroup');
        const colIndex = Array.from(th.parentElement?.children ?? []).indexOf(th);
        const col = colGroup?.children[colIndex] as HTMLElement | undefined;

        const onMouseMove = (ev: MouseEvent) => {
          const newWidth = Math.max(80, startWidth + ev.clientX - startX);

          if (col) {
            col.style.width = `${newWidth}px`;
          }

          th.style.width = `${newWidth}px`;
        };

        const onMouseUp = (ev: MouseEvent) => {
          const finalWidth = Math.max(80, startWidth + ev.clientX - startX);

          setColumnWidths((prev) => ({ ...prev, [colKey]: finalWidth }));
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
        };

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      },
    }),
    [],
  );

  const expandedRowKeys = useMemo(() => rows.map((row) => row.id), [rows]);

  const filteredRows = useMemo(() => {
    if (!searchText.trim()) {
      return rows;
    }

    const lower = searchText.toLowerCase();

    return rows
      .map((row) => {
        const campaignMatch =
          row.name?.toLowerCase().includes(lower) || row.displayName?.toLowerCase().includes(lower);
        const matchingChildren = row.children?.filter(
          (child) =>
            child.name?.toLowerCase().includes(lower) ||
            child.displayName?.toLowerCase().includes(lower),
        );

        if (campaignMatch) {
          return row;
        }

        if (matchingChildren && matchingChildren.length > 0) {
          return { ...row, children: matchingChildren };
        }

        return null;
      })
      .filter(Boolean) as TableData[];
  }, [rows, searchText]);

  const filteredExpandedRowKeys = useMemo(() => filteredRows.map((row) => row.id), [filteredRows]);

  const isAdform = dsp === DSP_ADFORM;
  // FreeWheel carries the same underlying fields, so these columns are shown.
  const isFreewheel = dsp === DSP_FREEWHEEL;
  const showLineItemDetailColumns = isAdform || isFreewheel;

  const columns: TableProps<TableData>['columns'] = [
    {
      dataIndex: 'enabled',
      title: '',
      width: 50,
      render: (value, row) =>
        row?.children ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Switch
              checked={value}
              onChange={(checked) => {
                updateStatus({
                  id: row.id,
                  enabled: checked,
                  advertiserId: currentAdvertiserId,
                });
              }}
            />
          </div>
        ) : (
          ''
        ),
      onCell: () => ({
        className: 'editable-cell',
      }),
    },
    // Read-only original DSP name (Adform + FreeWheel): the name columns below
    // edit display_name, so this is the only place a renamed row still shows
    // what the DSP calls it.
    ...(showLineItemDetailColumns
      ? [
          {
            dataIndex: 'name' as keyof TableData,
            title: isFreewheel ? 'FreeWheel Campaign / Line Item' : 'Adform Campaign / Line Item',
            width: columnWidths['adformName'] ?? 300,
            onHeaderCell: resizableHeaderCell('adformName', 300),
            ellipsis: true,
            render: (value: string | null | undefined, row: TableData) => (
              <div
                className={`px-3 py-2 text-base-sm font-normal leading-5 ${!row.children ? 'pl-6' : ''}`}
                title={value ?? ''}
              >
                {value ?? '-'}
              </div>
            ),
          },
        ]
      : []),
    // Campaign name column
    {
      dataIndex: 'displayName',
      title: showLineItemDetailColumns ? 'Campaign Name' : 'Campaign',
      width: showLineItemDetailColumns ? (columnWidths['campaignName'] ?? 256) : 1000,
      ...(showLineItemDetailColumns && { onHeaderCell: resizableHeaderCell('campaignName', 256) }),
      render: (value, row) =>
        // fw_campaign.display_name is ours and FreewheelCampaign.applyUpdates
        // persists it, so FreeWheel campaigns are renameable too.
        isAdform || isFreewheel ? (
          <Input
            defaultValue={row?.children ? value : '-'}
            placeholder="Campaign name..."
            disabled={!row?.children}
            onBlur={(e) => {
              const newValue = e.target.value;

              if (newValue !== value && row?.children) {
                setDraftCampaigns((prev) => ({
                  ...prev,
                  [row.id]: {
                    ...prev[row.id],
                    displayName: newValue,
                  },
                }));
                setHasDrafts(true);
              }
            }}
            className="w-full"
          />
        ) : (
          <div>{value}</div>
        ),
      onCell: () => ({
        className: 'editable-cell',
      }),
    },
    // Ad group name column (Adform + FreeWheel)
    ...(showLineItemDetailColumns
      ? [
          {
            dataIndex: 'displayName' as keyof TableData,
            key: 'adGroupName',
            title: 'Ad Group Name',
            width: columnWidths['adGroupName'] ?? 256,
            onHeaderCell: resizableHeaderCell('adGroupName', 256),
            render: (value: string, row: TableData) => (
              <Input
                defaultValue={!row?.children ? value : '-'}
                placeholder="Ad group name..."
                disabled={!!row?.children || !lineItemNamesEditable}
                onBlur={(e) => {
                  const newValue = e.target.value;

                  if (newValue !== value && !row?.children) {
                    setDraftLIs((prev) => ({
                      ...prev,
                      [row.id]: {
                        ...prev[row.id],
                        displayName: newValue,
                      },
                    }));
                    setHasDrafts(true);
                  }
                }}
                className="w-full"
              />
            ),
            onCell: () => ({
              className: 'editable-cell' as const,
            }),
          },
        ]
      : []),
    // Price Type column (Adform + FreeWheel line items)
    ...(showLineItemDetailColumns
      ? [
          {
            dataIndex: 'priceType',
            title: 'Price Type',
            width: 120,
            render: (value: string, row: TableData) =>
              !row?.children ? (
                <Select
                  value={value}
                  style={{ width: '100%' }}
                  onChange={(val) => {
                    setDraftLIs((prev) => ({
                      ...prev,
                      [row.id]: {
                        ...prev[row.id],
                        priceType: val,
                      },
                    }));
                    setHasDrafts(true);
                  }}
                >
                  {PRICE_TYPE_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              ) : (
                '-'
              ),
            onCell: () => ({
              className: 'editable-cell',
            }),
          },
        ]
      : []),
    // Media Type column (Adform + FreeWheel). FreeWheel uses Adform's media-type
    // list so both DSPs report under the same names. Until one is picked the
    // Select is empty, with FreeWheel's own type as the placeholder.
    ...(showLineItemDetailColumns
      ? [
          {
            dataIndex: 'mediaType',
            title: 'Media Type',
            width: 120,
            render: (value: string, row: TableData) =>
              !row?.children ? (
                <Select
                  value={value ?? undefined}
                  placeholder={
                    row.sourceMediaType ? `FreeWheel: ${row.sourceMediaType}` : 'Select media type'
                  }
                  style={{ width: '100%' }}
                  onChange={(val) => {
                    setDraftLIs((prev) => ({
                      ...prev,
                      [row.id]: {
                        ...prev[row.id],
                        mediaType: val,
                      },
                    }));
                    setHasDrafts(true);
                  }}
                >
                  {adformMediaTypes?.map((name) => (
                    <Option key={name} value={name}>
                      {name}
                    </Option>
                  ))}
                </Select>
              ) : (
                '-'
              ),
            onCell: () => ({
              className: 'editable-cell',
            }),
          },
        ]
      : []),
    // Audience Display Name column (Adform + FreeWheel)
    ...(showLineItemDetailColumns
      ? [
          {
            dataIndex: 'audienceDisplayName' as keyof TableData,
            key: 'audienceDisplayName',
            title: 'Audience Name',
            width: columnWidths['audienceDisplayName'] ?? 256,
            onHeaderCell: resizableHeaderCell('audienceDisplayName', 256),
            render: (value: string, row: TableData) => (
              <Input
                defaultValue={!row?.children ? (value ?? '') : '-'}
                placeholder={!row?.children ? (row.audienceName ?? 'Audience name...') : ''}
                disabled={!!row?.children || !lineItemNamesEditable}
                onBlur={(e) => {
                  const newValue = e.target.value;

                  if (newValue !== value && !row?.children) {
                    setDraftLIs((prev) => ({
                      ...prev,
                      [row.id]: {
                        ...prev[row.id],
                        audienceDisplayName: newValue || undefined,
                      },
                    }));
                    setHasDrafts(true);
                  }
                }}
                className="w-full"
              />
            ),
            onCell: () => ({
              className: 'editable-cell' as const,
            }),
          },
        ]
      : []),
    {
      dataIndex: priceFieldName,
      title: dsp === DSP_CRITEO ? 'CPC' : 'CPM',
      width: 120,
      render: (value, row) => (
        <InputNumber
          defaultValue={row.children ? '-' : value}
          min={0}
          precision={2}
          placeholder={row.children ? formatCurrency(0, currentAdvertiser?.currency || 'USD') : '-'}
          controls={false}
          disabled={!!row?.children}
          onChange={(val) => {
            setDraftLIs((prev) => ({
              ...prev,
              [row.id]: {
                ...prev[row.id],
                [priceFieldName]: val,
              },
            }));
            setHasDrafts(true);
          }}
          className="w-full full-height-input"
        />
      ),
      onCell: () => ({
        className: 'editable-cell',
      }),
    },
    ...(dsp === DSP_CRITEO || isAdform || isFreewheel
      ? [
          {
            dataIndex: 'budgetCap',
            title: 'Budget Cap',
            width: 120,
            render: (value: number, row: TableData) => (
              <InputNumber
                defaultValue={row.children ? value : '-'}
                min={0}
                precision={2}
                placeholder={
                  row.children ? '-' : formatCurrency(0, currentAdvertiser?.currency || 'USD')
                }
                controls={false}
                disabled={!row?.children}
                onChange={(val) => {
                  setDraftCampaigns((prev) => ({
                    ...prev,
                    [row.id]: {
                      ...prev[row.id],
                      budgetCap: typeof val === 'number' ? val : undefined,
                    },
                  }));
                  setHasDrafts(true);
                }}
                className="w-full full-height-input"
              />
            ),
            onCell: () => ({
              className: 'editable-cell',
            }),
          },
        ]
      : []),
  ];

  return (
    <div className="w-full h-full bg-white rounded-xl border border-primary-gray-100 flex-col justify-start items-start flex">
      <div className="p-8 flex-col gap-6 w-full">
        <div className="flex-col justify-start items-start gap-4 flex w-full">
          <div className="text-secondary-black text-heading-xl font-semibold leading-relaxed">
            {getDspSettingsTitle(dsp)}
          </div>
          <div className="flex justify-between items-center w-full">
            {supportsMetadataSync && (
              <div className="flex items-center gap-3">
                <Select
                  value={selectedEntityType}
                  onChange={(value) => setSelectedEntityType(value as string)}
                  style={{ width: 200 }}
                  placeholder="Select entity type"
                >
                  {entityOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
                <Button variant="primary" size="md" onClick={handleRefresh}>
                  Sync
                </Button>
              </div>
            )}
            <Button variant="primary" size="md" onClick={saveChanges} disabled={!hasDrafts}>
              Save changes
            </Button>
          </div>
          {isAdform && (
            <div className="w-full">
              <Input
                placeholder="Search campaigns or ad groups..."
                onChange={(e) => {
                  clearTimeout(searchTimerRef.current);

                  searchTimerRef.current = setTimeout(() => {
                    setSearchText(e.target.value);
                  }, 300);
                }}
                allowClear
                style={{ width: 320 }}
              />
            </div>
          )}
          <div className="grid w-full rounded-xl border border-primary-gray-100 p-4 pb-0">
            {isLoading && (
              <div className="fixed inset-0 z-50 bg-white/60 flex items-center justify-center">
                <Spin size="large" />
              </div>
            )}
            <Table
              className="dspSettings-table"
              dataSource={isAdform ? filteredRows : rows}
              columns={columns}
              rowKey="id"
              pagination={false}
              scroll={showLineItemDetailColumns ? { x: 1342 } : undefined}
              expandable={{
                expandedRowKeys: isAdform ? filteredExpandedRowKeys : expandedRowKeys,
                showExpandColumn: false,
                expandedRowClassName: () => 'ctv-expanded-row',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
